import {
    Badge,
    Button,
    Col,
    Container,
    Form,
    InputGroup,
    Modal,
    Pagination,
    Row,
    Spinner,
    Table,
    Toast,
    Breadcrumb
} from "react-bootstrap";
import {useContext, useEffect, useRef, useState} from "react";
import {BackendContext} from "../../context/Backend";
import styles from "./licensedusers.module.css";
import {useNavigate} from "react-router";
import {useTranslation} from "react-i18next";
import {CheckCircle, Search, X, ArrowUp, ArrowDown} from "react-bootstrap-icons";
import debounce from "lodash.debounce";
import {LIMIT} from "../../constants";

const LicensedUsers = () => {

    const backend = useContext(BackendContext);
    const navigate = useNavigate();
    const {t} = useTranslation();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState(null);
    const [sortField, setSortField] = useState("licenseExpiry");
    const [sortDir, setSortDir] = useState("ASC");

    const [userToRevoke, setUserToRevoke] = useState(null);
    const [userToExtend, setUserToExtend] = useState(null);
    const [extendDate, setExtendDate] = useState("");
    const [userToDelete, setUserToDelete] = useState(null);
    const [deleteEmailInput, setDeleteEmailInput] = useState("");

    const [toastMessage, setToastMessage] = useState(null);
    const [showToast, setShowToast] = useState(false);

    const searchInput = useRef();

    const fetchData = () => {
        setLoading(true);
        const offset = (page - 1) * LIMIT;

        const onSuccess = (response) => {
            setData(response);
            setLoading(false);
        };
        const onError = (error) => {
            navigate("/error", backend.api.getErrorState(error));
        };

        if (search) {
            backend.api.searchLicensedUsers(
                {search: search}, offset, LIMIT, sortField, sortDir, onSuccess, onError
            );
        } else {
            backend.api.getLicensedUsers(
                null, offset, LIMIT, sortField, sortDir, onSuccess, onError
            );
        }
    };

    useEffect(() => {
        fetchData();
    }, [page, search, sortField, sortDir]);

    const handleSearch = useRef(
        debounce((e) => {
            setPage(1);
            setSearch(e.target.value);
        }, 300)).current;

    const handleClear = () => {
        setSearch("");
        searchInput.current.value = "";
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDir(sortDir === "ASC" ? "DESC" : "ASC");
        } else {
            setSortField(field);
            setSortDir("ASC");
        }
        setPage(1);
    };

    const handleRevoke = () => {
        backend.api.updateUserLicense(
            {userId: userToRevoke.userId, action: "revoke"},
            () => {
                setUserToRevoke(null);
                setToastMessage(t('licensedUsers.revokeSuccessful'));
                setShowToast(true);
                fetchData();
            },
            (error) => {
                navigate("/error", backend.api.getErrorState(error));
            }
        );
    };

    const handleDelete = () => {
        backend.api.deleteLicensedUser(
            {userId: userToDelete.userId},
            () => {
                setUserToDelete(null);
                setDeleteEmailInput("");
                setToastMessage(t('licensedUsers.deleteSuccessful'));
                setShowToast(true);
                fetchData();
            },
            (error) => {
                navigate("/error", backend.api.getErrorState(error));
            }
        );
    };

    const handleExtend = () => {
        if (!extendDate) return;
        backend.api.updateUserLicense(
            {userId: userToExtend.userId, action: "extend", expiry: extendDate},
            () => {
                setUserToExtend(null);
                setExtendDate("");
                setToastMessage(t('licensedUsers.extendSuccessful'));
                setShowToast(true);
                fetchData();
            },
            (error) => {
                navigate("/error", backend.api.getErrorState(error));
            }
        );
    };

    const renderSortIcon = (field) => {
        if (sortField !== field) return null;
        return sortDir === "ASC"
            ? <ArrowUp className={styles.sortIndicator}/>
            : <ArrowDown className={styles.sortIndicator}/>;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        return new Date(dateStr).toLocaleDateString();
    };

    return (
        <Container fluid={true} className={styles.pageContainer}>
            <Row>
                <Col className={"d-flex justify-content-start"}>
                    <Breadcrumb className={"mt-3"}>
                        <Breadcrumb.Item href="#/">{t('breadcrumbs.home')}</Breadcrumb.Item>
                        <Breadcrumb.Item active>{t('nav.licensedUsers')}</Breadcrumb.Item>
                    </Breadcrumb>
                </Col>
            </Row>
            <Row>
                <Col className={"justify-content-center d-flex"}>
                    <Form>
                        <InputGroup className={styles.searchbar + " rounded-2 bg-primary"}>
                            <Search size={"2rem"} color={"white"} className={"p-2 m-1 align-bottom"}/>
                            <Form.Control
                                type="text"
                                onChange={handleSearch}
                                ref={searchInput}
                            />
                            <X size={"2rem"} color={"white"} className={"p-2 m-1 align-bottom"} onClick={handleClear}/>
                        </InputGroup>
                    </Form>
                </Col>
            </Row>
            <Row>
                <Col className={"d-flex justify-content-center"}>
                    <Toast className={"shadow-none"} onClose={() => setShowToast(false)}
                           delay={3000} show={showToast} bg={"success"} autohide>
                        <Toast.Body className={"success py-0 px-2"}>
                            <CheckCircle className={"m-1 align-bottom"}/>{toastMessage}
                        </Toast.Body>
                    </Toast>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Table responsive hover={true}>
                        <thead>
                        <tr className={"text-uppercase"}>
                            <th className={styles.label} onClick={() => handleSort("login")}>
                                {t('licensedUsers.tableLogin')}{renderSortIcon("login")}
                            </th>
                            <th className={styles.label} onClick={() => handleSort("name")}>
                                {t('licensedUsers.tableName')}{renderSortIcon("name")}
                            </th>
                            <th className={styles.label} onClick={() => handleSort("email")}>
                                {t('licensedUsers.tableEmail')}{renderSortIcon("email")}
                            </th>
                            <th className={styles.label} onClick={() => handleSort("licenseExpiry")}>
                                {t('licensedUsers.tableLicenseExpiry')}{renderSortIcon("licenseExpiry")}
                            </th>
                            <th className={styles.label}>
                                {t('licensedUsers.tableActions')}
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={5} className={"border-bottom-0"}>
                                    <Container className={"d-flex border-0 justify-content-center"}>
                                        <Spinner animation={"border"} size="lg"/>
                                    </Container>
                                </td>
                            </tr>
                        ) : (
                            <>
                                {data && data.users && data.users.length > 0 ? (
                                    data.users.map((user) => (
                                        <tr key={user.userId} className={user.licenseExpired ? styles.expired : ""}>
                                            <td className={styles.cell}>{user.login}</td>
                                            <td className={styles.cell}>
                                                {user.firstName + " " + user.lastName}
                                                {user.licenseExpired && <Badge bg="danger" className={styles.expiredBadge}>{t('licensedUsers.expired')}</Badge>}
                                            </td>
                                            <td className={styles.cell}>{user.email}</td>
                                            <td className={styles.cell}>{formatDate(user.licenseExpiry)}</td>
                                            <td>
                                                {!user.licenseExpired && <Button
                                                    className={styles.actionBtn + " websobutton"}
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => setUserToRevoke(user)}
                                                >
                                                    {t('licensedUsers.revokeButton')}
                                                </Button>}
                                                <Button
                                                    className={styles.actionBtn + " websobutton"}
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => {
                                                        setUserToExtend(user);
                                                        setExtendDate(user.licenseExpiry ? user.licenseExpiry.substring(0, 10) : "");
                                                    }}
                                                >
                                                    {t('licensedUsers.extendButton')}
                                                </Button>
                                                <Button
                                                    className={styles.actionBtn + " websobutton"}
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => {
                                                        setUserToDelete(user);
                                                        setDeleteEmailInput("");
                                                    }}
                                                >
                                                    {t('licensedUsers.deleteButton')}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={5} className={"text-center border-bottom-0"}>
                                            {t('licensedUsers.noLicensedUsers')}
                                        </td>
                                    </tr>
                                )}
                                {data && data.count > LIMIT && (
                                    <tr className={"paging"}>
                                        <td colSpan={5} className={"border-bottom-0"}>
                                            <Pagination size={"sm"}>
                                                {Array.from(
                                                    {length: Math.ceil(data.count / LIMIT)},
                                                    (_, i) => i + 1
                                                ).map((number) => (
                                                    <Pagination.Item
                                                        key={number}
                                                        active={number === page}
                                                        onClick={() => setPage(number)}
                                                    >
                                                        {number}
                                                    </Pagination.Item>
                                                ))}
                                            </Pagination>
                                        </td>
                                    </tr>
                                )}
                            </>
                        )}
                        </tbody>
                    </Table>
                </Col>
            </Row>

            {/* Revoke Modal */}
            <Modal
                show={userToRevoke != null}
                onHide={() => setUserToRevoke(null)}
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title className={"text-danger"}>{t('licensedUsers.revokeModalHeader')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {t('licensedUsers.revokeModalContent')} {userToRevoke?.firstName} {userToRevoke?.lastName}?
                </Modal.Body>
                <Modal.Footer>
                    <Button className={"websobutton"} onClick={() => setUserToRevoke(null)}>
                        {t('system.noButton')}
                    </Button>
                    <Button className={"websobutton"} variant="danger" onClick={handleRevoke}>
                        {t('system.yesButton')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Extend Modal */}
            <Modal
                show={userToExtend != null}
                onHide={() => { setUserToExtend(null); setExtendDate(""); }}
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t('licensedUsers.extendModalHeader')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{t('licensedUsers.extendModalContent')} {userToExtend?.firstName} {userToExtend?.lastName}</p>
                    <Form.Control
                        type="date"
                        value={extendDate}
                        onChange={(e) => setExtendDate(e.target.value)}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button className={"websobutton"} onClick={() => { setUserToExtend(null); setExtendDate(""); }}>
                        {t('system.cancelButton')}
                    </Button>
                    <Button className={"websobutton"} variant="primary" disabled={!extendDate} onClick={handleExtend}>
                        {t('system.saveButton')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Modal */}
            <Modal
                show={userToDelete != null}
                onHide={() => { setUserToDelete(null); setDeleteEmailInput(""); }}
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title className={"text-danger"}>{t('licensedUsers.deleteModalHeader')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{t('licensedUsers.deleteModalContent')} <strong>{userToDelete?.firstName} {userToDelete?.lastName}</strong></p>
                    <Form.Control
                        type="text"
                        placeholder={t('licensedUsers.deleteEmailPlaceholder')}
                        value={deleteEmailInput}
                        onChange={(e) => setDeleteEmailInput(e.target.value)}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button className={"websobutton"} onClick={() => { setUserToDelete(null); setDeleteEmailInput(""); }}>
                        {t('system.cancelButton')}
                    </Button>
                    <Button
                        className={"websobutton"}
                        variant="danger"
                        disabled={deleteEmailInput !== userToDelete?.email}
                        onClick={handleDelete}
                    >
                        {t('system.deleteButton')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default LicensedUsers;
